import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ShieldCheck, Zap, ArrowRight } from "lucide-react";
import { EASE } from "@/components/washflow/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1604335398980-ededcadcc37d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHxffGxhdW5kcnklMjBzaG9wJTIwd2FzaGluZyUyMG1hY2hpbmVzJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzkyNzYxNzQxfDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { icon: MessageCircle, value: "100%", label: "Digital receipts on WhatsApp & email" },
  { icon: ShieldCheck, value: "0", label: "Lost garments — every item tracked" },
  { icon: Zap, value: "Real-time", label: "Updates for customers & owners" },
];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.1, delay: 0.25 + i * 0.16, ease: EASE } }),
};

const fadeVariant = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: 0.85 + i * 0.12, ease: EASE } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
        <motion.img
          src={HERO_IMG}
          alt="Modern laundry shop with rows of washing machines"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="w-full h-[115%] object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F2B3E]/95 via-[#1B4F72]/85 to-[#1B4F72]/55" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
        <motion.span
          data-testid="hero-badge"
          variants={fadeVariant}
          custom={0}
          initial="hidden"
          animate="show"
          className="inline-block text-xs font-bold uppercase tracking-widest text-[#1B4F72] bg-[#AED6F1] px-4 py-1.5 rounded-full mb-8"
        >
          A proposal from OpsIQ
        </motion.span>
        <h1 data-testid="hero-headline" className="font-display font-extrabold tracking-tight text-white">
          <span className="block overflow-hidden">
            <motion.span variants={lineVariant} custom={0} initial="hidden" animate="show" className="block text-6xl sm:text-8xl lg:text-9xl leading-[0.95]">
              Wash<span className="text-[#1ABC9C]">Flow</span>
            </motion.span>
          </span>
          <span data-testid="hero-tagline" className="block mt-6 sm:mt-8">
            <span className="block overflow-hidden">
              <motion.span variants={lineVariant} custom={1} initial="hidden" animate="show" className="block text-2xl sm:text-4xl lg:text-5xl leading-tight text-[#EBF5FB]">
                Run your laundry business smarter,
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span variants={lineVariant} custom={2} initial="hidden" animate="show" className="block text-2xl sm:text-4xl lg:text-5xl leading-tight text-[#EBF5FB]">
                from <span className="text-[#1ABC9C]">pickup to delivery.</span>
              </motion.span>
            </span>
          </span>
        </h1>
        <motion.p variants={fadeVariant} custom={1} initial="hidden" animate="show" className="mt-6 max-w-xl text-base sm:text-lg text-[#AED6F1] leading-relaxed">
          One simple platform that tracks every garment, messages every customer, and shows you exactly how your business is doing — from your phone.
        </motion.p>
        <motion.div variants={fadeVariant} custom={2} initial="hidden" animate="show" className="mt-9 flex flex-col sm:flex-row gap-4">
          <motion.a
            href="#contact"
            data-testid="hero-get-started-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            className="inline-flex items-center justify-center gap-2 bg-[#1ABC9C] hover:bg-[#16A085] text-white text-lg font-bold px-8 py-4 rounded-full transition-colors shadow-lg shadow-[#1ABC9C]/30"
          >
            Get Started <ArrowRight className="w-5 h-5" />
          </motion.a>
          <motion.a
            href="#pricing"
            data-testid="hero-pricing-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/40 text-white text-lg font-bold px-8 py-4 rounded-full transition-colors"
          >
            See the Plans
          </motion.a>
        </motion.div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              data-testid={`hero-stat-${i}`}
              variants={fadeVariant}
              custom={3 + i}
              initial="hidden"
              animate="show"
              className="flex items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5"
            >
              <s.icon className="w-8 h-8 text-[#1ABC9C] shrink-0" />
              <div>
                <p className="font-display font-extrabold text-2xl text-white">{s.value}</p>
                <p className="text-sm text-[#AED6F1]">{s.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
